import React, { useState, useEffect, useRef, useMemo } from "react";
import { Collapse } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import locationActions from "../../../../_actions/locationActions";
import _ from "lodash";
import MultiselectTableV2 from "../../../_common/MultiselectTableV2";

export default function LocationMultiselect(props) {
  const {
    projectId,
    wpType,
    locationIds,
    setLocationIds,
    getDeselectAllowed,
    toggleDeselectNotAllowedAlert,
    deselectCallback,
  } = props;

  const dispatch = useDispatch();

  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );

  // Only fetch fresh projLocationList if none found
  useEffect(() => !projLocationList && dispatch(locationActions.getAll(projectId)), []);

  const locationTypeList = useSelector(
    (state) => state.feederLists.locationTypeList || []
  );

  const _projLocationList = useMemo(() => {
    return (projLocationList || []).map((l) => {
      const locType = (locationTypeList || []).find(
        (lt) => lt.locationTypeId === l.locationTypeId
      );
      return { ...l, locationTypeDescription: locType ? locType.description : "(N/A)" };
    });
  }, [projLocationList]);

  const columns = useRef([
    {
      path: "name",
      label: "Location Name",
      isSortable: true,
      isSearchable: true,
      width: "60%",
    },
    {
      path: "locationTypeDescription",
      label: "Type",
      isSortable: true,
      isSearchable: true,
      width: "30%",
      content: (loc) => <small>{loc.locationTypeDescription}</small>,
    },
  ]);

  const [showSection, setShowSection] = useState(false);
  const toggleSection = () => setShowSection(!showSection);

  const [selectedLocations, setSelectedLocations] = useState(
    (_projLocationList || []).filter((l) => (locationIds || []).includes(l.locationId)) ||
      []
  );

  const [clientDeselectedLocationId, setClientDeselectedLocationId] = useState(null);
  const [disabledLocations, setDisabledLocations] = useState([]);

  // Update selectedLocations for the right panel.
  // Build selectedLocations from scratch whenever projLocationList or locationIds
  // is updated. projLocationList can be updated via projDataRefresh, and locationIds
  // is updated whenever a location is selected/deselected (note setSelectedLocations is
  // not called on select/deselect - it is only called here).
  useEffect(() => {
    let _selectedLocations = []; // Blank array to rebuild selectedLocations

    // Create a copy of props.locationIds so we can rearrange items
    // (e.g. for putting genLoc as first item)
    let _locationIds = [...locationIds];

    // Check if genLoc exists. If not, insert as the first item.
    const genLoc = (_projLocationList || []).find((l) => l.isGeneric);
    if (!genLoc) return;

    if (!disabledLocations.includes(genLoc.locationId)) setDisabledLocations([genLoc]);

    // If genLoc is not the first item, make it first
    const genLocIndex = _locationIds.findIndex((lid) => lid === genLoc.locationId);
    if (genLocIndex !== 0) {
      _locationIds = [
        genLoc.locationId,
        ..._locationIds.slice(0, genLocIndex),
        ..._locationIds.slice(genLocIndex + 1),
      ];
    }

    // Loop thru incoming locationIds to apply changes. Push (not unshift) new item.
    _locationIds.forEach((lid) => {
      if (!(_selectedLocations || []).some((sl) => sl.locationId === lid)) {
        const locToAdd = (_projLocationList || []).find((l) => l.locationId === lid);
        if (locToAdd) _selectedLocations = [..._selectedLocations, locToAdd];
      }
    });
    setSelectedLocations(_selectedLocations);

    // Finally, if genLoc is not part of _locationIds, add and update
    if (genLocIndex < 0) setLocationIds(_locationIds);
  }, [_projLocationList, locationIds]);

  return (
    <div className="card m-2">
      <div className="card-body">
        <p
          className={`pointer-cursor mb-${showSection ? "2" : "0"}`}
          onClick={toggleSection}
        >
          <FontAwesomeIcon
            className="fa-fw text-muted"
            icon={showSection ? "caret-down" : "caret-right"}
          />
          <b>{`Locations for This ${wpType}: `}</b>
          {`(${locationIds.length} selected, expand to edit)`}
        </p>
        <Collapse isOpen={showSection}>
          <div className="d-flex flex-row">
            <LocationMultiselect_Left>
              <MultiselectTableV2
                className="table-loc-list"
                data={_projLocationList || []}
                idPropName="locationId"
                columns={columns.current}
                updateSelectedItemIds={setLocationIds}
                selectedItemIds={locationIds}
                disabledItemIds={(disabledLocations || []).map((dl) => dl.locationId)}
                clientDeselectedItemId={clientDeselectedLocationId}
                setClientDeselectedItemId={setClientDeselectedLocationId}
                getDeselectAllowed={getDeselectAllowed}
                toggleDeselectNotAllowedAlert={toggleDeselectNotAllowedAlert}
                deselectCallback={deselectCallback}
                searchType="simple"
                searchPlaceholder="Search name/type..."
              />
            </LocationMultiselect_Left>
            <LocationMultiselect_Right>
              {(selectedLocations || []).map((l, index) => (
                <LocationMultiselect_Right_Item
                  key={`ccoLocRow_${index}`}
                  location={l}
                  setClientDeselectedLocationId={setClientDeselectedLocationId}
                />
              ))}
            </LocationMultiselect_Right>
          </div>
        </Collapse>
      </div>
    </div>
  );
}

function LocationMultiselect_Left(props) {
  return (
    <div className="d-flex flex-column flex-fill pr-2" style={{ width: "50%" }}>
      <div className="card flex-grow-1">
        <div className="card-body p-3">
          <h5 className="mb-2 text-muted">Available Locations</h5>
          <div>{props.children}</div>
        </div>
      </div>
    </div>
  );
}

function LocationMultiselect_Right(props) {
  return (
    <div className="d-flex flex-column flex-fill" style={{ width: "50%" }}>
      <div className="card flex-grow-1 bg-lightgray loc-rt">
        <div className="card-body pb-2 p-3">
          <h5 className="text-muted my-0">Selected Locations</h5>
          {props.children}
        </div>
      </div>
    </div>
  );
}

function LocationMultiselect_Right_Item(props) {
  const { location, setClientDeselectedLocationId } = props;
  return (
    <div className="card my-2 mx-0">
      <div className="card-body p-2">
        <div className="d-flex flex-row w-100">
          <div className="loc-rt__item-head--name my-auto">
            <b>{location.name}</b>
          </div>
          <div className="loc-rt__item-head--unassign">
            <div className="float-right">
              <button
                name="unassign"
                id={`unassignBtn_${location.locationId}`}
                className={`btn btn-sm p-1 btn-outline-${
                  location.isGeneric ? "secondary" : "danger"
                }`}
                onClick={() => setClientDeselectedLocationId(location.locationId)}
                disabled={location.isGeneric}
              >
                <FontAwesomeIcon
                  icon={`${location.isGeneric ? "lock" : "times"}`}
                  className="fa-fw"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
